import { useContext } from "react";
import { useLocalization } from "@fluent/react";
import { ContextForm } from "cerulean";
import type { EnrollmentFormContext } from "../types";

// Specifically ONLY applies validation on a field that currently has an error.
const useRevalidateErroringField = (field: string) => {
  const { l10n } = useLocalization();
  const { data, metadata, dispatchMetadata } = (useContext(
    ContextForm.FormContext
  ) ?? {}) as EnrollmentFormContext;

  return () => {
    // Only revalidate the field if it has an error.
    const fieldMetadata = metadata?.[field];
    if (!fieldMetadata?.error) return;

    const value = data?.[field];
    const isEmpty = [null, undefined, ""].includes(value);

    if (isEmpty) {
      const nextError = fieldMetadata.required
        ? l10n.getString("error-required")
        : undefined;

      if (nextError === fieldMetadata.error) return;

      // If the field is empty and it's required and had a different error, update the error.
      dispatchMetadata?.({
        type: "update",
        update: {
          ...metadata,
          [field]: { ...fieldMetadata, error: nextError },
        },
      });
      return;
    }

    const validationError = fieldMetadata.validate?.(value ?? "", data, data);

    const nextError = validationError || undefined;
    // If the error hasn't changed, leave it be.
    if (nextError === fieldMetadata.error) return;

    dispatchMetadata?.({
      type: "update",
      update: {
        ...metadata,
        [field]: { ...fieldMetadata, error: nextError },
      },
    });
  };
};

export default useRevalidateErroringField;
